import { useState, useEffect, useRef, useCallback } from 'react'

const parseDuration = (text) => {
  const match = text?.match(/(\d+)\s*(?:hour|hr)s?\s*(?:and\s*)?(?:(\d+)\s*(?:minute|min)s?)?|(\d+)\s*(?:minute|min)s?|(\d+)\s*(?:second|sec)s?/i)
  if (!match) return 0
  const hrs  = parseInt(match[1] ?? 0)
  const mins = parseInt(match[2] ?? match[3] ?? 0)
  const secs = parseInt(match[4] ?? 0)
  return hrs * 3600 + mins * 60 + secs
}

const formatTime = (secs) => {
  const m = Math.floor(secs / 60).toString().padStart(2, '0')
  const s = (secs % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

const Timer = ({ duration }) => {
  const [remaining, setRemaining] = useState(duration)
  const [running,   setRunning]   = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    setRemaining(duration)
    setRunning(false)
  }, [duration])

  useEffect(() => {
    if (!running) { clearInterval(ref.current); return }
    ref.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { clearInterval(ref.current); setRunning(false); return 0 }
        return r - 1
      })
    }, 1000)
    return () => clearInterval(ref.current)
  }, [running])

  const pct = duration > 0 ? (remaining / duration) * 100 : 0

  return (
    <div className="mt-4 flex flex-col items-center gap-3">
      <div className="relative w-24 h-24">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e8e4f0" strokeWidth="2.5" />
          <circle
            cx="18" cy="18" r="15.9" fill="none" stroke="#2d1b5e" strokeWidth="2.5"
            strokeDasharray={`${pct} ${100 - pct}`}
            strokeLinecap="round"
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-xl font-bold font-mono text-text-primary">
          {formatTime(remaining)}
        </span>
      </div>
      <button
        onClick={() => running ? setRunning(false) : (remaining === 0 ? setRemaining(duration) : setRunning(true))}
        className="px-6 py-2 rounded-full bg-primary text-white text-sm font-semibold"
      >
        {running ? 'Pause' : remaining === 0 ? 'Restart' : 'Start'}
      </button>
    </div>
  )
}

export const CookMode = ({ recipe, onClose }) => {
  const [step, setStep] = useState(0)
  const steps = Array.isArray(recipe.steps)
    ? recipe.steps.map((s) => (typeof s === 'string' ? s : s.text ?? s.instruction ?? JSON.stringify(s)))
    : []

  const text      = steps[step] ?? ''
  const duration  = parseDuration(text)
  const total     = steps.length

  // Wake lock
  useEffect(() => {
    let wl = null
    const lock = async () => {
      try { wl = await navigator.wakeLock?.request('screen') } catch { /* no support */ }
    }
    lock()
    return () => { wl?.release() }
  }, [])

  return (
    <div className="fixed inset-0 z-50 bg-surface flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-safe pt-4 pb-4 border-b border-border">
        <button onClick={onClose} className="text-text-muted hover:text-text-primary">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
        <h2 className="text-sm font-semibold text-text-primary">{recipe.title}</h2>
        <span className="text-sm text-text-muted">{step + 1}/{total}</span>
      </div>

      {/* Step content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 py-6 overflow-y-auto">
        <div className="text-xs font-semibold text-text-muted uppercase tracking-widest mb-6">
          Step {step + 1}
        </div>

        <p className="text-2xl leading-relaxed text-text-primary text-center font-medium">
          {text}
        </p>

        {duration > 0 && <Timer key={step} duration={duration} />}
      </div>

      {/* Navigation */}
      <div className="flex gap-4 px-8 pb-safe pb-8 pt-4">
        <button
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="flex-1 py-3 rounded-2xl border-2 border-border text-text-primary font-semibold text-lg disabled:opacity-30 transition-opacity"
        >
          ← Prev
        </button>
        <button
          onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
          disabled={step === total - 1}
          className="flex-1 py-3 rounded-2xl bg-primary text-white font-semibold text-lg disabled:opacity-30 transition-opacity"
        >
          Next →
        </button>
      </div>

      {step === total - 1 && (
        <div className="text-center pb-4 text-emerald-600 font-semibold text-sm">
          All done! Enjoy your meal 🎉
        </div>
      )}
    </div>
  )
}
